import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, orgAPI, userAPI, billingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Zap,
  Home,
  User,
  BarChart3,
  Users,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  Building2,
  TrendingUp,
  DollarSign,
  Plus,
  UserPlus,
  Package,
  ClipboardList
} from 'lucide-react';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [newOrg, setNewOrg] = useState({ org_name: '', slug: '', plan: 'free' });
  
  const [showUserForm, setShowUserForm] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'company_admin', org_id: '' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, orgsRes, usersRes] = await Promise.all([
          adminAPI.getStats(),
          orgAPI.list(),
          userAPI.list(),
        ]);
        setStats(statsRes.data);
        setOrganizations(orgsRes.data);
        setUsers(usersRes.data);
      } catch (error) {
        toast.error('Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCreateOrg = async () => {
    if (!newOrg.org_name || !newOrg.slug) {
      toast.error('Name and slug are required');
      return;
    }
    
    try {
      const response = await orgAPI.create(newOrg);
      setOrganizations([...organizations, response.data]);
      setNewOrg({ org_name: '', slug: '', plan: 'free' });
      setShowOrgForm(false);
      toast.success('Organization created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Name, email, and password are required');
      return;
    }
    
    try {
      const response = await userAPI.create(newUser);
      setUsers([...users, response.data]);
      setNewUser({ name: '', email: '', password: '', role: 'company_admin', org_id: '' });
      setShowUserForm(false);
      toast.success('User created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin', active: true },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/admin/manage-artifacts' },
    { icon: <Package className="w-5 h-5" />, label: 'Catalog', path: '/admin/artifacts' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/admin/orders' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item, index) => (
              <li key={index}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                    location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-3" />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Super Admin Dashboard
            </h1>
            <span className="text-sm px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full font-medium">
              Super Admin
            </span>
          </div>
        </header>

        <main className="p-6" data-testid="admin-dashboard">
          {/* Platform Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="stat-card">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-600 mb-3">
                <Building2 className="w-5 h-5" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats?.total_organizations || 0}</p>
              <p className="text-sm text-slate-500">Organizations</p>
            </div>
            <div className="stat-card">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-100 text-emerald-600 mb-3">
                <Users className="w-5 h-5" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats?.active_users || 0}</p>
              <p className="text-sm text-slate-500">Active Users</p>
            </div>
            <div className="stat-card">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-100 text-amber-600 mb-3">
                <TrendingUp className="w-5 h-5" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats?.total_leads || 0}</p>
              <p className="text-sm text-slate-500">Total Leads</p>
            </div>
            <div className="stat-card">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-100 text-purple-600 mb-3">
                <DollarSign className="w-5 h-5" />
              </div>
              <p className="text-2xl font-bold text-slate-900">${stats?.monthly_revenue?.toFixed(0) || 0}</p>
              <p className="text-sm text-slate-500">Monthly Revenue (Mock)</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Organizations */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Organizations ({organizations.length})
                </h3>
                <Button size="sm" onClick={() => setShowOrgForm(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-org-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>

              {showOrgForm && (
                <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                  <div className="space-y-3">
                    <Input
                      placeholder="Organization name"
                      value={newOrg.org_name}
                      onChange={(e) => setNewOrg({ ...newOrg, org_name: e.target.value })}
                      data-testid="new-org-name"
                    />
                    <Input
                      placeholder="Slug (e.g., acme-corp)"
                      value={newOrg.slug}
                      onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value.toLowerCase().replace(/\s/g, '-') })}
                      data-testid="new-org-slug"
                    />
                    <select
                      className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                      value={newOrg.plan}
                      onChange={(e) => setNewOrg({ ...newOrg, plan: e.target.value })}
                    >
                      <option value="free">Free</option>
                      <option value="basic">Basic ($5/user)</option>
                      <option value="pro">Pro ($10/user)</option>
                    </select>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleCreateOrg} className="bg-indigo-600 hover:bg-indigo-700">
                        Create
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowOrgForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {organizations.length === 0 ? (
                  <div className="px-6 py-8 text-center text-slate-500">
                    No organizations yet
                  </div>
                ) : (
                  organizations.map((org) => {
                    const orgUsers = users.filter(u => u.org_id === org.org_id);
                    return (
                      <div key={org.org_id} className="px-6 py-4 hover:bg-slate-50" data-testid={`org-row-${org.org_id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-900">{org.org_name}</p>
                            <p className="text-sm text-slate-500">/{org.slug}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">
                              {org.plan}
                            </span>
                            <p className="text-xs text-slate-400 mt-1">{orgUsers.length} users</p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Users */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Users ({users.length})
                </h3>
                <Button size="sm" onClick={() => setShowUserForm(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-user-btn">
                  <UserPlus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>

              {showUserForm && (
                <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                  <div className="space-y-3">
                    <Input
                      placeholder="Full name"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      data-testid="new-user-name"
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      data-testid="new-user-email"
                    />
                    <Input
                      type="password"
                      placeholder="Password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      data-testid="new-user-password"
                    />
                    <select
                      className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                      value={newUser.role}
                      onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    >
                      <option value="super_admin">Super Admin</option>
                      <option value="company_admin">Company Admin</option>
                      <option value="employee">Employee</option>
                    </select>
                    <select
                      className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                      value={newUser.org_id}
                      onChange={(e) => setNewUser({ ...newUser, org_id: e.target.value })}
                    >
                      <option value="">No Organization</option>
                      {organizations.map((org) => (
                        <option key={org.org_id} value={org.org_id}>{org.org_name}</option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleCreateUser} className="bg-indigo-600 hover:bg-indigo-700">
                        Create
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowUserForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {users.length === 0 ? (
                  <div className="px-6 py-8 text-center text-slate-500">
                    No users yet
                  </div>
                ) : (
                  users.map((u) => {
                    const org = organizations.find(o => o.org_id === u.org_id);
                    return (
                      <div key={u.user_id} className="px-6 py-4 hover:bg-slate-50" data-testid={`user-row-${u.user_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                              <span className="text-indigo-600 font-semibold text-sm">{u.name?.charAt(0).toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{u.name}</p>
                              <p className="text-sm text-slate-500">{u.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              u.role === 'super_admin' ? 'bg-indigo-100 text-indigo-700' :
                              u.role === 'company_admin' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            } capitalize`}>
                              {u.role?.replace('_', ' ')}
                            </span>
                            {org && <p className="text-xs text-slate-400 mt-1">{org.org_name}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
